import { css } from '@bts-web/utils-style-engine';
import { storage } from '@bts-web/utils-storage';
import { FC, useEffect, useRef, useCallback } from 'react';

interface OfferLoaderElementProps {
  duration: number;
  size?: number;
  onComplete?: () => void;
  onRefocus?: () => void;
  beforeCompleteCallback?: () => void; // Called before completion; the amount of time before is specified by beforeCompleteCallbackMs
  beforeCompleteCallbackMs?: number;
}

export const OfferLoaderElement: FC<OfferLoaderElementProps> = ({
  duration,
  size = 14,
  onComplete,
  onRefocus,
  beforeCompleteCallback,
  beforeCompleteCallbackMs,
}) => {
  const progressRef = useRef(100);

  const intervalRef = useRef<number | null>(null);

  const startTimeRef = useRef<number | null>(null);

  const svgRef = useRef<SVGSVGElement | null>(null);

  const hasFocusBeenLostOnce = useRef(
    storage.getItem('hasFocusBeenLostOnce') === 'true',
  ); // Persist across unmounts

  const beforeCompleteCallbackRef = useRef(false);

  const updateProgress = useCallback(() => {
    if (!startTimeRef.current) return;

    const elapsedTime = Date.now() - startTimeRef.current;

    const absDuration = Math.abs(duration);

    const newProgress = Math.max(100 - (elapsedTime / absDuration) * 100, 0);

    progressRef.current = newProgress;

    // * Call beforeCompleteCallback before completion; nr of ms before is specified by beforeCompleteCallbackMs
    if (
      beforeCompleteCallback &&
      beforeCompleteCallbackMs &&
      !beforeCompleteCallbackRef.current &&
      absDuration - elapsedTime <= beforeCompleteCallbackMs &&
      absDuration - elapsedTime > 0
    ) {
      beforeCompleteCallbackRef.current = true;

      beforeCompleteCallback();
    }

    if (svgRef.current) {
      svgRef.current.setAttribute('data-progress', newProgress.toFixed(2));

      const strokeWidth = size / 12;

      const radius = size / 2 - strokeWidth;

      const circumference = 2 * Math.PI * radius;

      const strokeDashoffset = circumference * ((100 - newProgress) / 100);

      const circle = svgRef.current.querySelector('circle');

      if (circle) {
        circle.setAttribute('stroke-dashoffset', strokeDashoffset.toString());
      }
    }

    if (newProgress <= 0) {
      if (onComplete) onComplete();

      if (intervalRef.current) cancelAnimationFrame(intervalRef.current);
    } else {
      intervalRef.current = requestAnimationFrame(updateProgress);
    }
  }, [duration, size, onComplete, beforeCompleteCallback]);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) cancelAnimationFrame(intervalRef.current);

    progressRef.current = 100;

    startTimeRef.current = Date.now();

    beforeCompleteCallbackRef.current = false;

    intervalRef.current = requestAnimationFrame(updateProgress);
  }, [updateProgress]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (hasFocusBeenLostOnce.current && onRefocus) {
          onRefocus();
        }

        hasFocusBeenLostOnce.current = true;

        storage.setItem('hasFocusBeenLostOnce', 'true'); // Persist across unmounts

        startCountdown();
      } else if (document.visibilityState === 'hidden' && intervalRef.current) {
        cancelAnimationFrame(intervalRef.current);
      }
    };

    const handleBeforeUnload = () => {
      storage.removeItem('hasFocusBeenLostOnce'); // Clear local storage on page unload so that it does not affect the next trade
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.addEventListener('beforeunload', handleBeforeUnload);

    if (document.visibilityState === 'visible') {
      startCountdown();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      window.removeEventListener('beforeunload', handleBeforeUnload);

      if (intervalRef.current) cancelAnimationFrame(intervalRef.current);
    };
  }, [startCountdown, onComplete]);

  const strokeWidth = size / 12;

  const radius = size / 2 - strokeWidth;

  const circumference = 2 * Math.PI * radius;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      aria-label="offer loader indicator"
      data-progress="100"
    >
      <circle
        stroke="blue"
        fill="transparent"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset="0"
        r={radius}
        cx={size / 2}
        cy={size / 2}
        className={css({
          stroke: 'neutrals.fill_primary',
        })}
      />
    </svg>
  );
};
